import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Whether an element is laid out and on-screen. Disambiguates between multiple
 * DOM nodes sharing a selector (e.g. a duplicated button where the inactive one
 * is hidden or translated off-screen). Checks `display:none`,
 * `visibility:hidden`, `opacity:0`, zero-size boxes, and out-of-viewport
 * positioning.
 */
function isElementVisible(el: HTMLElement): boolean {
  const style = getComputedStyle(el);
  if (style.display === 'none') return false;
  if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
  // `opacity` can be '' when unset (treat as fully opaque); only 0 means hidden.
  if (style.opacity !== '' && Number(style.opacity) === 0) return false;

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  return !(
    rect.bottom <= 0 ||
    rect.right <= 0 ||
    rect.left >= viewportWidth ||
    rect.top >= viewportHeight
  );
}

/**
 * Resolves the best anchor for a selector — when it matches multiple elements,
 * prefer the first visible one so the coach-mark never anchors to a
 * hidden/offscreen duplicate. Falls back to the first match when none report
 * visible (e.g. jsdom, or before layout). Reports the index of the selector
 * that won, so the caller knows how much priority sits above the anchor it is
 * holding.
 */
function queryVisibleAnchor(selectors: readonly string[]): {
  element: HTMLElement | null;
  index: number;
} {
  // No selectors (e.g. `useAnchorElement([])`, or a filtered-empty list) means
  // nothing to anchor to. Bailing here also keeps an empty string out of
  // `document.querySelectorAll('')`, which throws a `SyntaxError`.
  if (!selectors.length) return { element: null, index: -1 };

  let fallback: HTMLElement | null = null;
  let fallbackIndex = -1;

  // A CSS selector list is resolved in DOM order. Accept an ordered selector
  // list instead, so callers can explicitly prefer an active-layout anchor
  // over a visible fallback that happens to render earlier in the DOM.
  for (const [index, selector] of selectors.entries()) {
    const matches = document.querySelectorAll<HTMLElement>(selector);
    const first = matches[0];
    if (!fallback && first) {
      fallback = first;
      fallbackIndex = index;
    }
    for (const el of matches) {
      if (isElementVisible(el)) return { element: el, index };
    }
  }
  return { element: fallback, index: fallbackIndex };
}

/**
 * Whether a selector ahead of `index` in the priority list has any match at
 * all. Plain `querySelector` with no layout reads, so the sticky fast path can
 * rule out an upgrade without re-running the full visibility walk.
 */
function hasHigherPriorityMatch(selectors: readonly string[], index: number): boolean {
  for (let i = 0; i < index; i += 1) {
    const selector = selectors[i];
    if (selector && document.querySelector(selector)) return true;
  }
  return false;
}

/**
 * Finds a coach-mark anchor by selector, kept fresh via a MutationObserver so
 * late mounts (tab switches, dropdown portals) and unmounts are tracked. Shared
 * extraction of the pattern in `DemoCoachMarks` / `HeidiVerifySpotlight`.
 *
 * An ordered-selector twin of this hook lives at
 * `packages/growth/src/ui/post-note-quality/spotlights/use-anchor-element.ts`;
 * behaviour changes here usually want mirroring there.
 */
export function useAnchorElement(
  selector: string | readonly string[]
): HTMLElement | null {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const elementRef = useRef<HTMLElement | null>(null);
  // Index of the selector the current anchor resolved from; `-1` when nothing
  // is held. Tells `check()` how much priority sits above the current anchor.
  const currentIndexRef = useRef(-1);
  // `.filter(Boolean)` drops empty entries so `useAnchorElement([])` and a
  // conditional list like `[speakerSel, micSel].filter(Boolean)` reduced to
  // nothing stay inert instead of reaching `querySelectorAll('')`.
  // `typeof` (not `Array.isArray`, which widens a `readonly string[]` to
  // `any[]`) keeps the list typed through the filter.
  const list: readonly string[] = typeof selector === 'string' ? [selector] : selector;
  const normalised = list.filter(Boolean);
  // Identity key only — never parsed back. NUL, not ',', so `'a,b'` and
  // `['a','b']` do not share a key. A selector may contain a comma.
  const selectorKey = normalised.join('\u0000');
  const selectors = useMemo(
    () => normalised,
    [selectorKey] // eslint-disable-line react-hooks/exhaustive-deps -- keyed by the joined selector list
  );

  useEffect(() => {
    // The index belongs to the previous selector list. Reset so a new list
    // full-resolves instead of probing with a stale position.
    currentIndexRef.current = -1;
    const check = () => {
      const current = elementRef.current;
      const currentIndex = currentIndexRef.current;
      const currentSelector = currentIndex >= 0 ? selectors[currentIndex] : undefined;
      // Sticky fast path: keep the current anchor unless a *higher-priority*
      // selector has gained a match. Ruling that out costs one plain
      // `querySelector` per selector above the current one and no layout read,
      // where the full resolve walks every selector with `querySelectorAll`
      // plus `getComputedStyle` / `getBoundingClientRect` on each match — on
      // every coalesced body mutation, for as long as the anchor is mounted.
      // An anchor resolved at index 0 (every single-selector caller) has no
      // higher-priority selector to probe, so its old semantics are unchanged.
      if (
        current?.isConnected &&
        currentSelector &&
        current.matches(currentSelector) &&
        isElementVisible(current) &&
        !hasHigherPriorityMatch(selectors, currentIndex)
      ) {
        return;
      }
      const { element: found, index } = queryVisibleAnchor(selectors);
      currentIndexRef.current = index;
      if (found !== current) {
        elementRef.current = found;
        setElement(found);
      }
    };

    check();

    // The observer watches the whole `body` subtree, so on a busy page (e.g. a
    // streaming note) mutations can fire in rapid bursts. `check()` can do
    // forced layout reads (`getComputedStyle` + `getBoundingClientRect`), so
    // coalesce bursts into a single check per animation frame to avoid layout
    // thrashing.
    let rafId: number | null = null;
    const scheduleCheck = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        check();
      });
    };

    const observer = new MutationObserver(scheduleCheck);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [selectors]);

  return element;
}
